import { useEffect, useState } from 'react'
import { useQuery } from '@/proxy/react-query'

import { client } from '@/proxy/client'

/**
 * @deprecated
 *
 * This hook is deprecated because it reimplements logic that now exists natively
 * in our `@/proxy/react-query` abstraction.
 *
 * Previously, this hook allowed triggering a GraphQL query manually by calling
 * `setVariables()` and internally toggled the `enabled` flag for React Query.
 * However, this pattern is now redundant — `useQuery` from `@/proxy/react-query`
 * supports the same behavior via its built-in options (e.g. `enabled`, `queryKey`,
 * and dynamic variables) and provides better type safety, caching, and error handling.
 *
 * **Migration:**
 * - Replace this hook with a direct call to `useQuery` from `@/proxy/react-query`.
 * - Use React Query's `enabled` and `refetch` APIs to control when the query runs.
 *
 * Example before:
 * ```js
 * const [setVariables, result] = useLazyQuery({ key: 'user', query: USER_QUERY })
 * setVariables({ id: 123 })
 * ```
 *
 * Example after:
 * ```js
 * const result = useQuery({
 *   queryKey: ['user', { id }],
 *   queryFn: ({ signal }) => client.request({ document: USER_QUERY, variables: { id }, signal }),
 *   enabled: !!id,
 * })
 * ```
 */
export default function ({ key, query, queryOptions = {} }) {
  const [enabled, setEnabled] = useState(false)
  const [variables, setVariables] = useState(null)

  useEffect(() => {
    if (variables !== null) setEnabled(true)
  }, [variables])

  const result = useQuery({
    queryKey: [key, variables, query],
    queryFn: async ({ signal }) => {
      const data = await client.request({ document: query, signal, variables })

      setEnabled(false)

      return data
    },
    enabled,
    ...queryOptions,
  })

  return [setVariables, result]
}
